// Audio context for notification sounds
let audioContext = null;
let audioContextResumePromise = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Ensure audio context is ready (handles suspended state properly)
const ensureAudioContextReady = async () => {
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') {
    // Avoid multiple concurrent resume calls
    if (!audioContextResumePromise) {
      audioContextResumePromise = ctx.resume().finally(() => {
        audioContextResumePromise = null;
      });
    }
    await audioContextResumePromise;
  }
  return ctx;
};

// Modern minimalistic notification sound - clean single "ping" (like Notion/Linear)
export const playNotificationSound = async () => {
  try {
    const ctx = await ensureAudioContextReady();

    const currentTime = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    // Clean, bell-like sine tone at G6 (1568 Hz)
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1568, currentTime);
    // Slight pitch bend down for warmth
    osc.frequency.exponentialRampToValueAtTime(1480, currentTime + 0.15);

    // Soft attack, smooth natural decay
    gain.gain.setValueAtTime(0, currentTime);
    gain.gain.linearRampToValueAtTime(0.25, currentTime + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.001, currentTime + 0.18);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(currentTime);
    osc.stop(currentTime + 0.2);

  } catch (e) {
    console.warn('Could not play notification sound:', e);
  }
};

// Subtle soft "pop" for messages in current conversation
export const playInChatSound = async () => {
  try {
    const ctx = await ensureAudioContextReady();

    const currentTime = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    // Very soft, low pop sound
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, currentTime);
    osc.frequency.exponentialRampToValueAtTime(200, currentTime + 0.06);

    // Quick, subtle envelope
    gain.gain.setValueAtTime(0, currentTime);
    gain.gain.linearRampToValueAtTime(0.12, currentTime + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.001, currentTime + 0.08);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(currentTime);
    osc.stop(currentTime + 0.1);

  } catch (e) {
    console.warn('Could not play in-chat sound:', e);
  }
};

// Distinctive urgent alert - repeated ascending pattern (more attention-grabbing)
export const playUrgentSound = async () => {
  try {
    const ctx = await ensureAudioContextReady();

    const currentTime = ctx.currentTime;

    const playTone = (freq, startTime, volume = 0.35) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(volume, startTime + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.15);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(startTime);
      osc.stop(startTime + 0.18);
    };

    // First sequence: E6 -> G6 -> B6 (ascending major arpeggio)
    playTone(1318.51, currentTime, 0.35);        // E6
    playTone(1567.98, currentTime + 0.1, 0.35);  // G6
    playTone(1975.53, currentTime + 0.2, 0.4);   // B6

    // Second sequence (repeat after short pause for emphasis)
    playTone(1318.51, currentTime + 0.45, 0.35); // E6
    playTone(1567.98, currentTime + 0.55, 0.35); // G6
    playTone(1975.53, currentTime + 0.65, 0.4);  // B6

  } catch (e) {
    console.warn('Could not play urgent sound:', e);
  }
};
